import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { SectionCard } from '@/components/common/SectionCard';
import { ROUTES } from '@/lib/constants/routes';
import type { DataRoomCurrentAccess } from '@/lib/types/data-room';
import { formatDate } from '@/lib/utils/date.utils';
import { BILLING_STRINGS } from '../constants/billing-strings';

const COPY = BILLING_STRINGS.PACKAGE;

/** One labelled fact, laid out like the plan card's facts. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <dt className="text-text-muted w-36 shrink-0 text-xs font-medium tracking-wide uppercase">{label}</dt>
      <dd className="text-text-primary text-sm">{children}</dd>
    </div>
  );
}

/** Either the live package, or the date it ended. Exactly one is given. */
export type PremiumPackageCardProps =
  | { access: DataRoomCurrentAccess; endedAt?: never }
  | { access?: never; endedAt: string };

/**
 * The SR NEXT Premium package on Billing & Info (FR-136, MYN-257).
 *
 * Premium is an admin grant with no Stripe object (A-107), so this card has
 * no invoice, payment method, add-on seat or cancel control, by design: it
 * says what the startup has, until when, and where seats are managed. Once
 * Premium has ended it says so and offers the Issue Report instead.
 */
export function PremiumPackageCard(props: PremiumPackageCardProps) {
  if (props.endedAt !== undefined) {
    return (
      <SectionCard title={COPY.ENDED_TITLE}>
        <p className="text-text-primary text-sm">{COPY.ENDED_BODY.replace('{date}', formatDate(props.endedAt))}</p>
        <Button asChild className="mt-4 inline-flex">
          <Link to={ROUTES.BILLING_PURCHASE}>{COPY.GET_ISSUE_REPORT}</Link>
        </Button>
      </SectionCard>
    );
  }

  const { access } = props;
  return (
    <SectionCard title={COPY.TITLE}>
      <dl className="flex flex-col gap-3">
        <Fact label={COPY.ENDS_LABEL}>{formatDate(access.endsAt)}</Fact>
        <Fact label={COPY.DAYS_LEFT_LABEL}>{access.daysLeft}</Fact>
        <Fact label={COPY.SEATS_LABEL}>{COPY.SEATS_VALUE.replace('{count}', String(access.seatLimit))}</Fact>
      </dl>
      <p className="text-text-muted mt-4 text-xs">{COPY.NOTE}</p>
      <Button asChild variant="link" className="mt-4 block w-fit">
        <Link to={ROUTES.STARTUP_TEAM}>{COPY.MANAGE_SEATS}</Link>
      </Button>
    </SectionCard>
  );
}
